// src/merchant/MerchantProducts.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  collection, query, where, getDocs,
  deleteDoc, doc, updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useApp } from '../context/AppContext';
import {
  Plus, Pencil, Trash2, ToggleLeft, ToggleRight, Search, Package,
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function MerchantProducts() {
  const { user }  = useApp();
  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [deleting, setDeleting] = useState(null);

  const fetchProducts = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // ── Only fetch products belonging to this merchant ──────────
      const q    = query(
        collection(db, 'products'),
        where('merchantId', '==', user.uid)
      );
      const snap = await getDocs(q);
      setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, [user]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setDeleting(id);
    try {
      await deleteDoc(doc(db, 'products', id));
      setProducts((p) => p.filter((x) => x.id !== id));
      toast.success('Product deleted');
    } catch { toast.error('Delete failed'); }
    finally { setDeleting(null); }
  };

  const toggleActive = async (product) => {
    try {
      const newVal = !product.isActive;
      await updateDoc(doc(db, 'products', product.id), { isActive: newVal });
      setProducts((p) =>
        p.map((x) => x.id === product.id ? { ...x, isActive: newVal } : x)
      );
      toast.success(newVal ? 'Product is now live ✅' : 'Product hidden');
    } catch { toast.error('Update failed'); }
  };

  const filtered = products.filter((p) =>
    !search ||
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.category?.toLowerCase().includes(search.toLowerCase())
  );

  const liveCount   = products.filter((p) => p.isActive !== false).length;
  const hiddenCount = products.length - liveCount;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-display font-bold text-xl text-gray-900">My Products</h2>
          <p className="text-gray-400 font-body text-sm">
            {products.length} total ·
            <span className="text-green-600 ml-1">{liveCount} live</span> ·
            <span className="text-gray-400 ml-1">{hiddenCount} hidden</span>
          </p>
        </div>
        <Link to="/merchant/products/add"
          className="inline-flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white px-5 py-2.5 rounded-2xl font-body font-semibold text-sm transition-all shadow-[0_4px_15px_rgba(244,63,94,0.3)]">
          <Plus size={18} /> Add Product
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search your products..."
          className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:border-rose-400 font-body text-sm text-gray-700" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-8 h-8 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Package size={40} className="text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 font-body font-medium mb-1">
              {search ? 'No products match your search' : 'No products yet'}
            </p>
            <p className="text-gray-300 font-body text-xs mb-4">
              {!search && 'Add your first product to start selling!'}
            </p>
            {!search && (
              <Link to="/merchant/products/add"
                className="inline-flex items-center gap-1.5 text-rose-500 hover:text-rose-600 font-body text-sm font-medium">
                <Plus size={14} /> Add your first product
              </Link>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {['Product', 'Category', 'Price', 'Stock', 'Status', 'Actions'].map((h) => (
                      <th key={h} className="px-5 py-3.5 text-left text-xs font-body font-semibold text-gray-500 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <img src={product.image || 'https://via.placeholder.com/48'} alt={product.name}
                            className="w-12 h-12 rounded-xl object-cover border border-gray-100 shrink-0" />
                          <div className="min-w-0">
                            <p className="font-body font-semibold text-gray-900 text-sm line-clamp-1">{product.name}</p>
                            <p className="text-gray-400 font-body text-xs line-clamp-1">{product.description?.slice(0, 45)}...</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="bg-rose-50 text-rose-600 text-xs font-body font-semibold px-2.5 py-1 rounded-full capitalize">
                          {product.category || '—'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <p className="font-display font-bold text-gray-900 text-sm">₹{product.price?.toLocaleString('en-IN')}</p>
                        {product.originalPrice > product.price && (
                          <p className="text-gray-400 text-xs line-through font-body">₹{product.originalPrice?.toLocaleString('en-IN')}</p>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-xs font-body font-semibold px-2.5 py-1 rounded-full ${
                          (product.stock || 0) > 5  ? 'bg-green-100 text-green-700' :
                          (product.stock || 0) > 0  ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {product.stock || 0} left
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <button onClick={() => toggleActive(product)}
                          className="flex items-center gap-1.5 text-sm font-body transition-colors">
                          {product.isActive !== false ? (
                            <><ToggleRight size={22} className="text-green-500" /><span className="text-green-600 text-xs font-semibold">Live</span></>
                          ) : (
                            <><ToggleLeft size={22} className="text-gray-400" /><span className="text-gray-400 text-xs font-semibold">Hidden</span></>
                          )}
                        </button>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <Link to={`/merchant/products/edit/${product.id}`}
                            className="w-8 h-8 bg-blue-50 hover:bg-blue-500 text-blue-500 hover:text-white rounded-xl flex items-center justify-center transition-all">
                            <Pencil size={14} />
                          </Link>
                          <button onClick={() => handleDelete(product.id, product.name)}
                            disabled={deleting === product.id}
                            className="w-8 h-8 bg-red-50 hover:bg-red-500 text-red-500 hover:text-white rounded-xl flex items-center justify-center transition-all disabled:opacity-50">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filtered.map((product) => (
                <div key={product.id} className="p-4 flex gap-3">
                  <img src={product.image || 'https://via.placeholder.com/56'} alt={product.name}
                    className="w-14 h-14 rounded-xl object-cover border border-gray-100 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-body font-semibold text-gray-900 text-sm line-clamp-1">{product.name}</p>
                    <p className="font-display font-bold text-rose-600 text-sm">₹{product.price?.toLocaleString('en-IN')}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Link to={`/merchant/products/edit/${product.id}`}
                        className="flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-2.5 py-1 rounded-full font-body font-medium">
                        <Pencil size={11} /> Edit
                      </Link>
                      <button onClick={() => handleDelete(product.id, product.name)}
                        className="flex items-center gap-1 text-xs bg-red-50 text-red-600 px-2.5 py-1 rounded-full font-body font-medium">
                        <Trash2 size={11} /> Delete
                      </button>
                      <button onClick={() => toggleActive(product)}
                        className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-body font-medium ${
                          product.isActive !== false ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'
                        }`}>
                        {product.isActive !== false ? 'Live' : 'Hidden'}
                      </button>
                    </div>
                  </div>
                  <span className={`text-xs font-body font-semibold px-2 py-0.5 h-fit rounded-full ${
                    (product.stock || 0) > 5 ? 'bg-green-100 text-green-700' :
                    (product.stock || 0) > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {product.stock || 0}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
